/**
 * Pre-play lag check.
 *
 * Before a player pays/joins, we time a handful of HTTP requests to the
 * server's `/api/ping` endpoint. The round-trip captures BOTH the
 * player's network RTT and any server event-loop saturation, so a high
 * result is the right signal for "this match will feel bad — warn them".
 *
 * This is HTTP-based on purpose: the in-game WebSocket latency meter
 * (GameScene.startLatencyMeter) only works once you've joined the room,
 * which is after payment. This runs before any of that.
 */

// Mirror the base-URL convention used by the payment flow
// (packages/client/src/payments/entry-fee.ts) so the probe hits the
// exact same host the entry fee + room join use.
const env = (import.meta as any).env;
const SERVER_HTTP_URL = (env?.VITE_SERVER_URL || 'ws://localhost:2567').replace(/^ws/, 'http');

/** Round-trip (ms) at/above which we warn the player before they play. */
export const LAG_WARN_MS = 600;

/** Per-request timeout. Past this we treat the server as unreachable. */
const PROBE_TIMEOUT_MS = 3000;

function median(values: number[]): number {
  if (values.length === 0) return Infinity;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

async function probeOnce(): Promise<number> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  const start = performance.now();
  try {
    const res = await fetch(`${SERVER_HTTP_URL}/api/ping`, {
      cache: 'no-store',
      signal: controller.signal,
    });
    if (!res.ok) return Infinity;
    // Drain the (tiny) body so the round-trip includes the full response.
    await res.text();
    return performance.now() - start;
  } catch {
    return Infinity; // network error / timeout / server down
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Measure latency to the game server. Fires `samples` sequential probes,
 * discards the first (TLS/connection warm-up skews it), and returns the
 * MEDIAN of the rest — robust to a single one-off spike. Returns
 * `Infinity` if the server can't be reached (treated as "bad" by callers).
 */
export async function measureLatency(samples = 5): Promise<number> {
  const results: number[] = [];
  for (let i = 0; i < samples; i++) {
    const ms = await probeOnce();
    // The first probe pays TLS/socket setup; ignore it unless it's the
    // only data we have (server unreachable on every attempt).
    if (i === 0) continue;
    results.push(ms);
  }
  if (results.length === 0) return Infinity;
  // If every real probe failed, surface Infinity (don't median over it).
  if (results.every((r) => r === Infinity)) return Infinity;
  return median(results.filter((r) => r !== Infinity));
}
